/**
 * useClients - TanStack Query hooks for SIG Clone client operations
 *
 * Features:
 * - Automatic caching with 5-minute cache time
 * - Background refetching when data becomes stale
 * - Optimistic updates for mutations
 * - Query invalidation on mutations
 *
 * @see packages/web-apps/monutrack/src/api/sig-legacy.ts
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  listClients,
  getClient,
  updateClient,
  getClientsDropdown,
} from '@/api/sig-legacy'
import type { ClientWithGrouping, ClientDropdownItem } from '@/types/sig-clone'

// ============================================================================
// QUERY KEYS
// ============================================================================

export const sigClientsKeys = {
  all: ['sig', 'clients'] as const,
  lists: () => [...sigClientsKeys.all, 'list'] as const,
  list: () => [...sigClientsKeys.lists()] as const,
  details: () => [...sigClientsKeys.all, 'detail'] as const,
  detail: (id: number) => [...sigClientsKeys.details(), id] as const,
  dropdown: () => [...sigClientsKeys.all, 'dropdown'] as const,
}

// ============================================================================
// QUERIES
// ============================================================================

/**
 * useClients - List all clients with grouping information
 *
 * @returns Client list with loading state and refetch function
 */
export function useClients() {
  const { data, isLoading, isFetching, error, refetch } = useQuery({
    queryKey: sigClientsKeys.list(),
    queryFn: listClients,
    staleTime: 5 * 60 * 1000, // 5 minutes
  })

  return {
    clients: data ?? [],
    loading: isLoading,
    isFetching,
    error: error instanceof Error ? error.message : null,
    refetch,
  }
}

/**
 * useClient - Get a single client by ID
 *
 * @param id - Client ID to fetch
 * @returns Single client with loading state and refetch function
 */
export function useClient(id: number | undefined) {
  const { data, isLoading, isFetching, error, refetch } = useQuery({
    queryKey: sigClientsKeys.detail(id!),
    queryFn: () => getClient(id!),
    enabled: !!id,
    staleTime: 5 * 60 * 1000,
  })

  return {
    client: data ?? null,
    loading: isLoading,
    isFetching,
    error: error instanceof Error ? error.message : null,
    refetch,
  }
}

/**
 * useClientsDropdown - Get clients for dropdown/select components
 *
 * @returns Client dropdown items with loading state
 */
export function useClientsDropdown() {
  const { data, isLoading, isFetching, error, refetch } = useQuery({
    queryKey: sigClientsKeys.dropdown(),
    queryFn: getClientsDropdown,
    staleTime: 10 * 60 * 1000, // 10 minutes (rarely changes)
  })

  return {
    clients: data ?? [],
    loading: isLoading,
    isFetching,
    error: error instanceof Error ? error.message : null,
    refetch,
  }
}

// ============================================================================
// MUTATIONS
// ============================================================================

/**
 * useUpdateClient - Update client grouping with optimistic updates
 *
 * @returns Mutation function and state
 */
export function useUpdateClient() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ id, clientePaiId }: { id: number; clientePaiId: number | null }) =>
      updateClient(id, clientePaiId),

    // Optimistic update
    onMutate: async ({ id, clientePaiId }) => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({ queryKey: sigClientsKeys.list() })

      // Snapshot the previous value
      const previousClients = queryClient.getQueryData<ClientWithGrouping[]>(
        sigClientsKeys.list()
      )

      // Optimistically update the cache
      if (previousClients) {
        queryClient.setQueryData<ClientWithGrouping[]>(
          sigClientsKeys.list(),
          previousClients.map((client) =>
            client.id === id
              ? { ...client, clientePaiId: clientePaiId ?? undefined }
              : client
          )
        )
      }

      return { previousClients }
    },

    // Rollback on error
    onError: (_err, _variables, context) => {
      if (context?.previousClients) {
        queryClient.setQueryData(sigClientsKeys.list(), context.previousClients)
      }
    },

    // Always refetch after error or success
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: sigClientsKeys.list() })
    },
  })
}
